/**
 * BookingDetailPage.jsx
 * Module: pages / Booking Detail
 *
 * THE single "Booking Details" page for a confirmed air-cargo booking (route
 * booking-history/air-booking-details), shared by every role. Fetches the
 * booking by queryId and renders the route banner, Booking Information,
 * Charge Breakdown (incl. GST), Shipment & Rates, Flight Details and
 * Shipment Dimensions sections.
 *
 * TWO MODES:
 *  • booking mode (default) — a confirmed/cancelled/awaited booking, loaded via
 *    single_booking_data. Everything above renders.
 *  • query mode (?view=query) — a query that has NOT been booked yet: Live
 *    Rates Available (Auto Quoted), Quote Pending, Quote Available (Manual
 *    Quoted) and its expired variant. Those rows carry no booking at all, so
 *    there is no AWB, carrier, charge breakdown, rate or flight itinerary to
 *    show; the page renders the same layout minus those sections rather than a
 *    wall of "N/A". The data is the admin/booking-history row itself (handed
 *    over through navigation state by pages/Admin/Queries.jsx), backfilled from
 *    search_cargo_result_detail/:queryId so a refresh or a pasted link still
 *    resolves. See resolveQueryStatus / the `isQueryMode` branches below.
 *
 * Unified view: KAM/admin used to get a completely different surface here (an
 * older AirCargoDetails component, rendered inline on /QueriesKAM; since
 * deleted). Both the customer's booking view and the KAM's "view booking" from
 * Queries now land on THIS page and see the identical layout — the ONLY
 * KAM/admin difference is the extra "Markup Details" section, which customers
 * never see.
 *
 * Layout is MUI: amber-accented section cards, KPI tiles and amber-header
 * tables. Those primitives (SectionCard, InfoCell, StatCard, ChargeGroup, …)
 * live in components/common/BookingDetailMuiUI.jsx and are shared with the
 * ocean detail view (components/BookingHistory/OceanCargoDetails.jsx), so the
 * two surfaces can't drift apart. Pure field resolvers are shared with it too,
 * via BookingDetailUI.jsx.
 *
 * Author: FreightSee frontend team
 * Date: 2026-07-06
 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  useTheme,
} from '@mui/material';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentsIcon      from '@mui/icons-material/Payments';
import FlightIcon        from '@mui/icons-material/Flight';
import StraightenIcon    from '@mui/icons-material/Straighten';
import Inventory2Icon    from '@mui/icons-material/Inventory2';
import AcUnitIcon        from '@mui/icons-material/AcUnit';
import PercentIcon       from '@mui/icons-material/Percent';
import { getsinglebookingdata, isBookingAccessDenied } from '../store/BookingHistory/BookingHistorySlice';
import { UNAUTHORIZED_BOOKING_REDIRECT_STATE } from '../components/common/UnauthorizedBookingModal';
import { getqueryId } from '../store/Queries/QueriesSlice';
import { get_single_search_query } from '../store/Admin/Queries/QuerySlice';
import { get_booking_markup, getAirlinesList } from '../store/Markup/markupSlice';
import { formatrates } from '../functions/Utils';
import { SPACE_AVAILABILITY_OPTIONS } from '../utils/constant';
import airlineDefault from '../assets/images/active-flight-icon.svg';
import {
  safeNum,
  calcPerKg,
  formatAWB,
  getFlag,
  parseSegDate,
  resolveStoredCommodity,
  resolveTemperatureRange,
  resolveProductCommodityDetails,
} from '../components/common/BookingDetailUI';
// The amber MUI design system, shared with the ocean detail view
// (components/BookingHistory/OceanCargoDetails.jsx) so the two booking
// surfaces can't drift apart again.
import {
  C,
  StatusPill,
  SectionCard,
  InfoSubLabel,
  InfoCell,
  StatCard,
  TaxableRow,
  ChargeGroup,
  NetAmountBar,
  TagChip,
  SegmentPill,
  BoolPill,
  thSx,
  tdSx,
} from '../components/common/BookingDetailMuiUI';

// ─── Utility helpers ────────────────────────────────────────────────────────

/**
 * Calculates total volumetric (dimensional) weight across a package list
 * using the IATA formula (L × W × H × units) / 6000, converting each
 * dimension to centimetres first based on its stored unit.
 *
 * @param {Array<{length:number,width:number,height:number,units:number,unit:string}>} pkgs
 * @returns {number|null} total volumetric weight in KG, or null if empty/zero
 */
const CM_FACTOR = { C: 1, CM: 1, M: 100, IN: 2.54, I: 2.54, INCHES: 2.54 };
const calcVolumeWeight = (pkgs) => {
  if (!pkgs?.length) return null;
  const total = pkgs.reduce((sum, pkg) => {
    const l = safeNum(pkg.length) || 0;
    const w = safeNum(pkg.width) || 0;
    const h = safeNum(pkg.height) || 0;
    const u = safeNum(pkg.units) || 1;
    const unit = (pkg.unit || 'C').toUpperCase();
    // ── IN mode: round each dim to nearest cm — matches the search form's
    // vol-weight formula (Math.round per dim). Using exact 2.54 gives a
    // slightly different result (~2488 vs 2469) due to cubing the decimal.
    const isInch = unit === 'IN' || unit === 'I' || unit === 'INCHES';
    const toCm = isInch ? (v) => Math.round(v * 2.54) : (v) => v * (CM_FACTOR[unit] || 1);
    return sum + (toCm(l) * toCm(w) * toCm(h) * u) / 6000;
  }, 0);
  return total > 0 ? total : null;
};

/**
 * Recomputes volumetric weight forcing every dimension through the inch→cm
 * conversion (Math.round(dim * 2.54)) regardless of the package's stored
 * unit.
 *
 * Why: old bookings can have pkg.unit saved as "CM" even when the shipment
 * was actually searched/quoted in inches. Trusting that stored unit would
 * run calcVolumeWeight's CM formula on inch dimensions, understating the
 * volumetric weight by ~16x (e.g. 20.7 kg instead of ~340 kg) and therefore
 * understating the chargeable weight/freight shown to the customer. This is
 * only called when the shipment's unit is known to be CFT/inches — see the
 * `volumeUnit === 'CFT'` guard in the derived-data section below.
 *
 * @param {Array<{length:number,width:number,height:number,units:number}>} pkgs
 * @returns {number|null} total volumetric weight in KG, or null if empty/zero
 */
const calcVolumeWeightInch = (pkgs) => {
  if (!pkgs?.length) return null;
  const total = pkgs.reduce((sum, pkg) => {
    const l = safeNum(pkg.length) || 0;
    const w = safeNum(pkg.width)  || 0;
    const h = safeNum(pkg.height) || 0;
    const u = safeNum(pkg.units)  || 1;
    if (l === 0 && w === 0 && h === 0) return sum;
    const lCm = Math.round(l * 2.54);
    const wCm = Math.round(w * 2.54);
    const hCm = Math.round(h * 2.54);
    return sum + (lCm * wCm * hCm * u) / 6000;
  }, 0);
  return total > 0 ? total : null;
};
// ─── Query-mode helpers ──────────────────────────────────────────────────────
// Only used when the page is opened on an unbooked query (?view=query). A query
// record is the admin/booking-history row, which stores its ports as display
// strings ("Delhi - (DEL)") and its dates in several shapes, so these normalise
// what the booking payload would otherwise have handed over ready-made.

/**
 * Pulls the IATA code out of a query record's airport label.
 *
 * admin/booking-history stores the airport as the picker's display string —
 * "Delhi - (DEL)" — whereas a booking carries the bare code on
 * `bookeditem.origin`. The parenthesised code is used when present; otherwise
 * the label is shown as-is rather than dropped, so an oddly-formatted record
 * still names its port.
 *
 * @param {string} label - e.g. "Delhi - (DEL)"
 * @returns {string} the IATA code ("DEL"), or the trimmed label, or ''
 */
const airportCode = (label) => {
  const s = String(label ?? '').trim();
  if (!s) return '';
  return (s.match(/\(([^)]+)\)/)?.[1] || s).trim();
};

/**
 * Parses any of the date shapes a query record carries: the goodsDetails
 * "DD/MM/YYYY" string the search form wrote, and the ISO timestamps
 * (cargoreadydate / createdAt / quoteExpiry) Mongo stores.
 *
 * @param {string} value - raw date from the query record
 * @returns {import('moment').Moment|null} parsed moment, or null when unusable
 */
const parseQueryDate = (value) => {
  if (!value) return null;
  const m = moment(value, ['DD/MM/YYYY', 'DD-MM-YYYY', moment.ISO_8601]);
  return m.isValid() ? m : null;
};

/**
 * Turns a query record's raw `Quote_status` into the label the Queries list
 * shows for the same row, so the status pill here and the badge there can't
 * disagree:
 *   Manual Quoted → "Quote Available"  (→ "Quote Expired" once quoteExpiry has
 *                                        passed and payment isn't confirmed)
 *   Auto Quoted   → "Live Rates Available"
 *   anything else → shown verbatim
 *
 * Mirrors the finalShownStatus logic in pages/Admin/Queries.jsx. The expiry
 * comparison is between two absolute instants, so it needs no timezone — the
 * list only reaches for moment-timezone because it also *renders* the expiry
 * in IST.
 *
 * @param {object|null} rec - the query record
 * @returns {string} display status, or '' when the record carries none
 */
const resolveQueryStatus = (rec) => {
  const raw = rec?.Quote_status;
  if (!raw) return '';
  const expiryRaw = rec?.quoteExpiry ?? rec?.queryExpiryDate ?? null;
  const isManualQuote =
    raw === 'Manual Quoted' || rec?.queryQuotingStatus === 'Manual' || rec?.isManualQuoted === true;
  const isExpired =
    isManualQuote &&
    !!expiryRaw &&
    moment(expiryRaw).isBefore(moment()) &&
    rec?.payment_status !== 'Payment Confirmed';
  if (isExpired) return 'Quote Expired';
  if (raw === 'Manual Quoted') return 'Quote Available';
  if (raw === 'Auto Quoted') return 'Live Rates Available';
  return raw;
};

/**
 * Flattens the `preferred_airline` list, which the search form saves either as
 * plain carrier codes or as the dropdown's `{ label, value }` option objects.
 *
 * @param {Array|undefined} list - raw preferred_airline value
 * @returns {string[]} display-ready carrier labels (possibly empty)
 */
const preferredAirlineLabels = (list) =>
  (Array.isArray(list) ? list : [])
    .map((a) =>
      typeof a === 'string' ? a : (a?.carrierName || a?.label || a?.name || a?.code || a?.value || '')
    )
    .map((s) => String(s).trim())
    .filter(Boolean);

/**
 * Whether this record is a manually quoted one (a KAM-priced quote, or a
 * booking made off one) rather than a live/auto-quoted rate.
 *
 * Manual quotes never carry the carrier's `productCommodityDetails` block —
 * nobody tendered the shipment to an airline API to get product/commodity
 * codes back — so this is what lets the Commodity Details section fall back to
 * the shipper's own commodity ("General Cargo") instead of vanishing. The flag
 * lives under a different key depending on which projection of the query the
 * booking payload embeds, hence the sweep: `queryQuotingStatus: "Manual"` on
 * the search query / queryDetails (as the Bookings lists and
 * BookingIntermediatrypage.jsx read it), `Quote_status: "Manual Quoted"` on a
 * query record, `isManualQuoted` from buildRateSearchPayload, or a plain
 * `manualQuoteId` reference.
 *
 * @param {object|null} d - single_booking_data payload, or a query record
 * @returns {boolean} true when the record came from a manual quote
 */
const isManualQuoteRecord = (d) => {
  if (!d || typeof d !== 'object') return false;
  return [d, d.search_query_data, d.searchquery, d.queryDetails, d.bookeditem].some(
    (s) =>
      s && typeof s === 'object' &&
      (s.queryQuotingStatus === 'Manual' ||
       s.Quote_status === 'Manual Quoted' ||
       s.isManualQuoted === true ||
       !!s.manualQuoteId)
  );
};

/**
 * Layers the full query document (search_cargo_result_detail) over the
 * admin/booking-history row the Queries list handed over.
 *
 * The two are different projections of the same query: the list row carries the
 * status fields (Quote_status, quoteExpiry, userDetails, organizationDetails)
 * that the full document doesn't, while the full document carries every field
 * the customer actually entered. Only defined keys are copied so the fuller
 * document can't blank out something the row already had — same rule as
 * mergeFullQueryDetail in pages/Admin/Queries.jsx.
 *
 * @param {object} prev - the list row (may be {})
 * @param {object|null} full - the full query document
 * @returns {object} merged query record
 */
const mergeQueryRecord = (prev, full) => {
  if (!full) return prev;
  const merged = { ...(prev || {}) };
  Object.keys(full).forEach((key) => {
    if (full[key] !== undefined) merged[key] = full[key];
  });
  return merged;
};

/**
 * The airline logo URL the booking itself stored, probed across every shape the
 * booking flows write it in: `ailinelogoUrl` (the misspelling the FS rate
 * options and BookCargo.jsx carry), `carrierLogo` (what
 * NewBookingFlowContent.jsx persists), and `logoLocation` (the airline master's
 * own field name, mirrored onto segments by NewsearchForm.jsx) — on the booked
 * item, its first itinerary segment, or the payload root.
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {string} logo URL, or '' when the booking stored none
 */
const resolveStoredAirlineLogo = (d) => {
  const booked = d?.bookeditem || {};
  const seg    = booked?.flightItineraries?.[0] || {};
  return (
    booked.ailinelogoUrl || booked.carrierLogo || booked.airlineLogo || booked.logoLocation ||
    seg.ailinelogoUrl    || seg.carrierLogo    || seg.airlineLogo    || seg.logoLocation ||
    d?.ailinelogoUrl     || d?.carrierLogo     || ''
  );
};

/**
 * The booking's carrier code ("LH"), used to look a logo up in the airline
 * master when the booking stored no logo URL of its own.
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {string} 2-letter carrier code, or '' when there is no itinerary
 */
const resolveCarrierCode = (d) => {
  const booked = d?.bookeditem || {};
  const seg    = booked?.flightItineraries?.[0] || {};
  return String(seg.carrierCode || seg.carr_code || booked.carrierCode || '').trim();
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the flight number the way the airline prints it — carrier code
 * followed by the number ("LH202"). FS-sourced segments already carry the code
 * inside `carrierNumber`, and some carriers repeat it, so the code is skipped
 * whenever the number's first two letters already match it (avoids "LHLH202").
 *
 * @param {object} seg - one flightItineraries entry
 * @returns {string} display-ready carrier number, or "—" when there is none
 */
const carrierLabel = (seg) => {
  const code = seg?.rates_from === 'FS' ? '' : (seg?.carrierCode || '');
  const num  = seg?.carrierNumber || '';
  if (!num) return '—';
  const alreadyPrefixed =
    code && num.slice(0, 2).toLowerCase() === code.slice(0, 2).toLowerCase();
  return `${alreadyPrefixed ? '' : code}${num}`;
};

/**
 * Collects the KAM/admin-only markup figures for the "Markup Details" section
 * into ready-to-render tiles.
 *
 * Two sources, in priority order:
 *  1. `nodeData` — the booking itself already carries what was actually applied
 *     to THIS booking (markupResult.markup / .markupRatePerKg /
 *     .minimum_markup_percentage, buyRate.rateperkillo, odcc_details, and
 *     bookeditem.rateDetails.markupPercentage). Same single fetch the rest of
 *     the page uses, so it needs no extra call and can't go stale.
 *  2. `markupConfig` — GET markup/booking_markup/:queryId, the *pre-booking*
 *     markup configuration (carrier / minMarkupPercentage / customer category).
 *     It can come back empty or stale once a booking is confirmed, which is why
 *     it only fills the gaps the booking itself leaves.
 *
 * Tiles with no value at all are dropped rather than rendered as "N/A", so
 * older bookings that predate these fields get a short section instead of a
 * wall of blanks.
 *
 * @param {object} nodeData - the single_booking_data payload
 * @param {object} markupConfig - the get_booking_markup payload (may be empty)
 * @returns {Array<{label: string, value: string, unit?: string, highlight?: boolean}>}
 */
const buildMarkupTiles = (nodeData, markupConfig) => {
  const markupResult = nodeData?.markupResult || {};
  const rateDetails  = nodeData?.bookeditem?.rateDetails || {};
  const odcc         = nodeData?.odcc_details || {};

  const pct = (v) => {
    const n = safeNum(v);
    return n != null ? String(Number(n.toFixed(2))) : null;
  };
  // Currency figures go through formatrates — the same ₹-prefixing, Indian
  // digit-grouping helper the charge breakdown above and the ocean page's
  // rate tiles already use, so the markup rates read like every other amount
  // on the page. It already carries the ₹, hence the plain "/ KG" units below.
  const rate = (v) => {
    const n = safeNum(v);
    return n != null ? formatrates(n.toFixed(2)) : null;
  };

  return [
    {
      label: 'Markup',
      value: pct(markupResult?.markup ?? rateDetails?.markupPercentage),
      unit: '%',
      highlight: true,
    },
    { label: 'Markup / KG',      value: rate(markupResult?.markupRatePerKg),   unit: '/ KG' },
    { label: 'Buy Rate',         value: rate(nodeData?.buyRate?.rateperkillo), unit: '/ KG' },
    {
      label: 'Min. Markup',
      value: pct(markupResult?.minimum_markup_percentage ?? markupConfig?.minMarkupPercentage),
      unit: '%',
    },
    {
      label: 'Carrier',
      value: markupConfig?.carrier
        || nodeData?.bookeditem?.flightItineraries?.[0]?.carrierCode
        || null,
    },
    {
      label: 'Customer Category',
      value: odcc?.customer_category || markupConfig?.customerCategory || null,
    },
    {
      label: 'ODCC Markup',
      value: pct(odcc?.markup_percentage ?? markupConfig?.odccStrategyPercentage),
      unit: '%',
    },
  ].filter((tile) => tile.value != null && tile.value !== '');
};

// ─── Loading skeleton ────────────────────────────────────────────────────────

const PageSkeleton = () => (
  <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-5">
    {[180, 160, 240, 120, 120].map((h, i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
        <div className={`bg-gray-100 rounded`} style={{ height: h }} />
      </div>
    ))}
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

/**
 * @returns {JSX.Element} the standalone booking-detail page for the
 *   `queryId` taken from the URL (falls back to Redux's stored queryId).
 *   Same layout for every role; KAM/admin additionally get Markup Details.
 */
const BookingDetailPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [searchParams] = useSearchParams();

  const queryIdFromUrl   = searchParams.get('queryId');
  const queryIdFromStore = useSelector(getqueryId);
  const queryId          = queryIdFromUrl || queryIdFromStore;

  // Which list opened this page. Queries.jsx appends `&from=queries` so the
  // back button can return there explicitly instead of relying on the history
  // stack; the Bookings lists pass nothing and fall through to navigate(-1).
  const openedFrom = searchParams.get('from');

  // `?view=query` — opened on a query that has no booking behind it yet (Live
  // Rates Available / Quote Pending / Quote Available / Quote Expired). See the
  // module header: the page then loads the query instead of the booking and
  // omits every booking-only section.
  const isQueryMode = searchParams.get('view') === 'query';
  // The admin/booking-history row Queries.jsx handed over, so the page paints
  // immediately and gets the status fields the full query document lacks. Absent
  // on a refresh or a pasted link — the fetch below covers that.
  const seedQueryRecord = location.state?.queryRecord || null;

  // KAM/admin get the SAME layout as the customer — the role only decides
  // whether the extra "Markup Details" section is rendered and where the back
  // button goes. "air_admin" counts as KAM-side, exactly as it does everywhere
  // else these booking pages branch on role (PageLayout's isKamSide,
  // OceanCargoDetails' isKamOrAdmin).
  const userroles = useSelector((state) => state.data?.data?.data?.user?.userroles);
  const isKamSide = userroles === 'kam' || userroles === 'air_admin';

  const [nodeData,     setNodeData]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState(null);
  // The carrier's logo resolved from the airline master, used only when the
  // booking stored no logo URL of its own — see the effect below.
  const [masterCarrierLogo, setMasterCarrierLogo] = useState('');
  // KAM-only: pre-booking markup config (GET markup/booking_markup/:queryId),
  // the same call Queries.jsx made before opening its old inline KAM view. Only
  // used to fill gaps the booking payload itself leaves — see buildMarkupTiles.
  const [markupConfig, setMarkupConfig] = useState({});

  // Read up here with the other hooks, not down in the derived-data section:
  // the loading/error guards below return early, so a useTheme() call after
  // them would run on some renders and not others — a hook-order violation.
  const isDark = useTheme().palette.mode === 'dark';

  // ── Data fetch ─────────────────────────────────────────────────

  /**
   * Fetches the booking by `queryId` and populates `nodeData`; redirects back
   * to the booking history list if no queryId is available at all, or if the
   * booking belongs to someone else (single_booking_data answers 404 "Booking
   * not found" — see isBookingAccessDenied), in which case the list page
   * raises the "not authorised" popup.
   * @returns {void}
   */
  const loadData = () => {
    // /booking-history isn't in the KAM/air_admin ACL, so bouncing them there
    // would render a blank page — send them to their own queries list instead.
    if (!queryId) { navigate(isKamSide ? '/QueriesKAM' : '/booking-history'); return; }
    // A seeded query already has everything the page needs to paint, so skip
    // the skeleton entirely and let the fetch below fill in the gaps in place.
    setLoading(!(isQueryMode && seedQueryRecord));
    setFetchError(null);

    // ── Query mode: there IS no booking to fetch. Start from the list row the
    // Queries page handed over (already the exact admin/booking-history record)
    // so the page paints instantly, then layer the full query document over it
    // for the fields the summary row omits. On a refresh/pasted link there's no
    // row, so the fetch is the only source and a failure is a real error.
    if (isQueryMode) {
      if (seedQueryRecord) setNodeData(seedQueryRecord);
      dispatch(get_single_search_query(queryId))
        .then((res) => {
          const full = res?.payload?.data?.nodeData;
          if (full) setNodeData((prev) => mergeQueryRecord(prev, full));
          else if (!seedQueryRecord) setFetchError('Query data not found.');
        })
        .catch(() => {
          if (!seedQueryRecord) setFetchError('Failed to load query details. Please try again.');
        })
        .finally(() => setLoading(false));
      return;
    }

    dispatch(getsinglebookingdata({ queryId }))
      .then((res) => {
        if (isBookingAccessDenied(res?.payload)) {
          // replace: true so the browser's back button doesn't walk straight
          // back into the booking they can't see. KAM/admin go to their queries
          // list (the bookings list isn't in their ACL and carries a customer-
          // worded "not authorised" popup that doesn't apply to them).
          navigate(
            isKamSide ? '/QueriesKAM' : '/booking-history',
            isKamSide
              ? { replace: true }
              : { replace: true, state: UNAUTHORIZED_BOOKING_REDIRECT_STATE }
          );
          return;
        }
        const d = res?.payload?.data?.data;
        if (d) {
          setNodeData(d);
        } else {
          setFetchError('Booking data not found.');
        }
      })
      .catch(() => setFetchError('Failed to load booking details. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [queryId, isKamSide, isQueryMode]); // eslint-disable-line

  // Airline logo fallback. Bookings made off a live FS/airline rate carry the
  // logo URL on the rate option itself, but manual-quote and older bookings
  // never stored one — the banner tile then fell back to the generic plane
  // icon. The airline master (GET /markup/listAirlines, the same list the
  // manual-quote screen uses for its carrier tabs) has a logo for the carrier
  // code, so it's matched up on demand: skipped entirely whenever the booking
  // already stored a URL, and whenever there's no carrier code to match on
  // (query mode, where no carrier has been chosen yet).
  useEffect(() => {
    setMasterCarrierLogo('');
    if (resolveStoredAirlineLogo(nodeData)) return;
    const code = resolveCarrierCode(nodeData);
    if (!code) return;
    let alive = true;
    dispatch(getAirlinesList())
      .then((res) => {
        if (!alive) return;
        const match = (res?.payload || []).find(
          (a) => String(a?.code || '').trim().toUpperCase() === code.toUpperCase()
        );
        // Failures and unmatched codes leave it '' so the tile keeps the
        // generic plane icon rather than a broken image.
        setMasterCarrierLogo(match?.logo || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [nodeData]); // eslint-disable-line

  // KAM/admin only: the pre-booking markup configuration, fetched alongside the
  // booking (never instead of it — the booking payload is still the primary
  // markup source, see buildMarkupTiles). Failures are swallowed on purpose:
  // this endpoint 404s for older bookings and the section falls back to the
  // figures the booking itself carries.
  // Skipped in query mode: nothing has been booked, so there are no applied
  // markup figures to pair this configuration with and the section is hidden.
  useEffect(() => {
    if (!isKamSide || !queryId || isQueryMode) return;
    dispatch(get_booking_markup(queryId))
      .then((res) => setMarkupConfig(res?.payload?.data || {}))
      .catch(() => {});
  }, [queryId, isKamSide, isQueryMode]); // eslint-disable-line

  /**
   * Back-button handler, in priority order:
   *
   *  1. `?from=queries` — opened from the Queries list, so go straight back to
   *     it: /QueriesKAM for KAM/admin, /queries for customers (both routes
   *     render the same Queries component, but only the role's own path is in
   *     its ACL). Explicit rather than history-based so it still lands there
   *     after a refresh (which resets the history index) or if anything
   *     redirected in between.
   *  2. The previous in-app page. React Router records a monotonic `idx` on
   *     window.history.state — > 0 means at least one earlier entry we
   *     navigated from, so this returns to whichever Bookings list opened the
   *     page.
   *  3. The booking-history list. When the page is opened cold from an email
   *     link there is no earlier entry (idx 0 / absent), so navigate(-1) would
   *     either do nothing or leave the app.
   *
   * @returns {void}
   */
  const handleBack = () => {
    if (openedFrom === 'queries') navigate(isKamSide ? '/QueriesKAM' : '/queries');
    else if (window.history.state?.idx > 0) navigate(-1);
    // /booking-history isn't in the KAM/air_admin ACL — that fallback would drop
    // them on an unregistered route (blank page), so send them to their queries
    // list instead.
    else navigate(isKamSide ? '/QueriesKAM' : '/booking-history');
  };

  // ── Guard states ───────────────────────────────────────────────

  if (loading) return <PageSkeleton />;

  if (fetchError) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Unable to load {isQueryMode ? 'query' : 'booking'}
        </h2>
        <p className="text-gray-500 mb-6">{fetchError}</p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={handleBack}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
            Go Back
          </button>
          <button onClick={loadData}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Derived data ───────────────────────────────────────────────

  const bookedItem   = nodeData?.bookeditem || {};
  // In query mode the record IS the search query — its packagingDetails /
  // goodsDetails / paymentTerms sit at the root, with no search_query_data
  // wrapper (the field exists but is null on an unbooked query).
  const searchQuery  = isQueryMode
    ? (nodeData || {})
    : (nodeData?.search_query_data || nodeData?.searchquery || {});
  const rateDetails  = bookedItem?.rateDetails || {};
  const segments     = bookedItem?.flightItineraries || [];
  const charges      = rateDetails?.chargesBreakDown || [];
  const shipTotal    = searchQuery?.packagingDetails?.shipmentTotal || {};
  const packages     = searchQuery?.packagingDetails?.packagingType?.packages || [];
  const totalPieces  = searchQuery?.packagingDetails?.packagingType?.totalunits
                    ?? shipTotal?.totalpieces;

  // Query records name their ports as "Delhi - (DEL)"; bookings carry the bare
  // code on bookeditem.
  const origin      = (isQueryMode
    ? airportCode(nodeData?.originPort?.originAirport)
    : bookedItem?.origin) || 'N/A';
  const destination = (isQueryMode
    ? airportCode(nodeData?.destinationPort?.destinationAirport)
    : bookedItem?.destination) || 'N/A';
  const carrierName = segments?.[0]?.carr_name || segments?.[0]?.carrierName || segments?.[0]?.carrierCode || 'N/A';
  // Space the booking was made against (1 = Space Available, 2 = Subject to
  // Availability), set on offline/manual-quote rates. Internal: shown under
  // Booking Details for KAM/admin only, and only when the booking carries it.
  const spaceAvailability =
    bookedItem?.space_availability ?? nodeData?.space_availability;
  const spaceAvailabilityLabel = SPACE_AVAILABILITY_OPTIONS.find(
    (o) => o.value === Number(spaceAvailability),
  )?.label;
  const awbNumber   = formatAWB(nodeData?.airWaybillNumber, segments?.[0]?.airlinePrefix);
  // Booking mode shows the booking's own lifecycle status; query mode shows the
  // quote status the Queries list badge shows for the same row.
  const bookingStatus = isQueryMode
    ? resolveQueryStatus(nodeData)
    : (nodeData?.booking_status || '');

  // Commodity + temperature both come straight out of the booking response
  // (goodsDetails.commodity_description and temperatureRange). "General Cargo"
  // is only used when the booking genuinely carries no commodity at all, and
  // the temperature range is omitted entirely for non-temperature-controlled
  // shipments rather than rendered as an empty/"N/A" chip.
  const commodityLabel  = resolveStoredCommodity(nodeData) || 'General Cargo';
  const temperatureText = resolveTemperatureRange(nodeData);

  // Dates — scheduledDepartureDate/scheduledArrivalDate come back as either
  // "DD-MMM-YYYY" (manual quotes) or full ISO (live FS responses), so
  // parseSegDate tries both formats instead of assuming one.
  const firstLegDepartureDate = segments[0]?.scheduledDepartureDate;
  // Query mode has no itinerary, so the banner's departure line falls back to
  // the cargo-ready date — the only date the shipper has committed to at this
  // stage. goodsDetails carries the form's local "DD/MM/YYYY" and is preferred
  // over the root ISO timestamp, whose UTC instant (18:30Z) renders as the
  // previous day.
  const cargoReadyDate = isQueryMode
    ? parseQueryDate(nodeData?.goodsDetails?.cargoreadydate ?? nodeData?.cargoreadydate)
    : null;
  const depShort = isQueryMode
    ? (cargoReadyDate ? cargoReadyDate.format('DD MMM') : '')
    : firstLegDepartureDate
      ? parseSegDate(firstLegDepartureDate).format('DD MMM')
      : '';
  const depFull  = firstLegDepartureDate
    ? parseSegDate(firstLegDepartureDate).format('DD MMM YYYY') : 'N/A';
  const arrShort = segments.length && segments[segments.length - 1]?.scheduledArrivalDate
    ? parseSegDate(segments[segments.length - 1].scheduledArrivalDate).format('DD MMM') : '';

  // Weights & volumes
  const chargeableWtUnit = rateDetails?.chargeableWeightUnit || 'KG';
  const totalWeight      = safeNum(shipTotal?.totalweight) ?? safeNum(rateDetails?.totalWeight);
  const weightUnit       = shipTotal?.weightunit || chargeableWtUnit;
  const volumeUnit       = shipTotal?.unit || 'CBM';
  const totalVolume      = safeNum(shipTotal?.totalvolume);

  // ── Volume weight resolution chain ────────────────────────────────────────
  // Priority order:
  //  1. totalvolweight  — exact value saved by recalcAllTotals (rounded-cm formula)
  //  2. totalvolumeweight / volumeWeight — legacy field names
  //  3. CFT→VW from totalvolume  — when volumeUnit is CFT this is more reliable
  //     than calcVolumeWeight(packages) because old bookings may have packages
  //     saved with unit:"CM" even though the search was in inches, making
  //     calcVolumeWeight return a ~16× too-small value (323 instead of ~2509).
  //  4. calcVolumeWeight(packages) — package-level calc (correct only when pkg.unit is right)
  //  5. CBM totalvolume formula — last resort
  // The > 0 guard ensures a stored 0 (empty-form default) falls to the next step.
  const _storedVW = safeNum(shipTotal?.totalvolweight);
  const _legacyVW1 = safeNum(shipTotal?.totalvolumeweight);
  const _legacyVW2 = safeNum(shipTotal?.volumeWeight);
  const volumeWtRaw =
    // ── CFT: package-level inch calc always takes priority over any stored ───
    // value. Stored totalvolweight for old inch bookings was computed as raw
    // in³/6000 (no cm conversion), producing ~16× too-small results such as
    // 151 kg instead of the correct ~2488 kg. calcVolumeWeightInch forces
    // Math.round(dim*2.54) per dimension regardless of pkg.unit stored in DB.
    (volumeUnit === 'CFT' ? calcVolumeWeightInch(packages) : null) ??
    (_storedVW  != null && _storedVW  > 0 ? _storedVW  : null) ??
    (_legacyVW1 != null && _legacyVW1 > 0 ? _legacyVW1 : null) ??
    (_legacyVW2 != null && _legacyVW2 > 0 ? _legacyVW2 : null) ??
    // ── CFT fallback: derive from stored totalvolume (approximate, ~±1%) ─────
    (volumeUnit === 'CFT' && totalVolume != null && totalVolume > 0
      ? totalVolume * 0.0283168 * 166.67
      : null) ??
    // ────────────────────────────────────────────────────────────────────────
    calcVolumeWeight(packages) ??
    (totalVolume != null
      ? totalVolume * 166.67    // CBM fallback (1 CBM = 166.67 kg)
      : null);
  // ─────────────────────────────────────────────────────────────────────────
  const volumeWt = volumeWtRaw != null ? Number(volumeWtRaw).toFixed(3) : null;

  // ── Chargeable weight: IN mode must ignore the API value (was computed ────
  // with CM dims). Recompute as max(GW, VW_in); ceil to match form footer.
  const chargeableWt =
    volumeUnit === 'CFT' && totalWeight != null && volumeWtRaw != null
      ? Math.ceil(Math.max(totalWeight, volumeWtRaw))
      : safeNum(rateDetails?.chargeableWeight) ??
        // Query records have no rateDetails — the search form's own figure is
        // stored on packagingDetails.shipmentTotal instead.
        safeNum(shipTotal?.chargeableWeight) ??
        (totalWeight != null && volumeWtRaw != null
          ? Math.ceil(Math.max(totalWeight, volumeWtRaw))
          : null);
  // ─────────────────────────────────────────────────────────────────────────

  const totalAmount  = safeNum(nodeData?.total_amount) ?? safeNum(rateDetails?.totalAmount);
  const ratePerkilo  = safeNum(rateDetails?.ratePerkilo);

  // Charge groups (preserve insertion order from API). GST is persisted as
  // just another chargesBreakDown entry (category "GST", added at booking
  // time in NewBookingFlowContent.jsx / BookingIntermediatrypage.jsx), so
  // grouping by category and singling out the "GST" key below is enough to
  // reconstruct Taxable Amount / GST Amount / Net Amount without a separate
  // GST field or API call.
  const groupedCharges = charges.reduce((acc, c) => {
    const key = c.category || c.chargeType || 'Other Charges';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});
  const chargeTypeKeys = Object.keys(groupedCharges);
  // Sums every charge within one category (e.g. all "Main" or "GST" lines).
  const groupTotal = (k) =>
    groupedCharges[k].reduce((ss, i) => ss + (safeNum(i.Totalamount ?? i.chargeAmount) || 0), 0);

  // Taxable Amount = Net Amount minus the GST category, i.e. what GST was
  // actually calculated on. When no "GST" group exists this equals the sum
  // of whatever groups ARE present (0 when there's no breakdown at all).
  const preGstSubtotal = chargeTypeKeys
    .filter((k) => k !== 'GST')
    .reduce((s, k) => s + groupTotal(k), 0);

  // Fallback GST — for bookings where hasBreakdown was false at checkout
  // (QR/direct-airline rate, or an empty chargesBreakDown), GST was still
  // calculated but never appended as its own chargesBreakDown line item —
  // see NewBookingFlowContent.jsx. It IS persisted as plain rateDetails
  // fields (taxableAmount/gstAmount) though, so fall back to those whenever
  // no "GST" category exists in the breakdown. Bookings that predate this
  // GST feature simply have no gstAmount field, so this fallback resolves
  // to 0 and they render exactly as before.
  const hasGstGroup = chargeTypeKeys.includes('GST');
  const fallbackGstAmount = hasGstGroup ? 0 : safeNum(rateDetails?.gstAmount) || 0;
  const fallbackTaxableAmount = hasGstGroup
    ? 0
    : chargeTypeKeys.length > 0
      ? preGstSubtotal
      : safeNum(rateDetails?.taxableAmount) ?? Math.max(0, (totalAmount || 0) - fallbackGstAmount);

  // Net Amount = every charge across every category, GST included, plus the
  // fallback GST amount when GST wasn't itemised as its own category. Falls
  // back to the backend's stored totalAmount (already GST-inclusive — see
  // NewBookingFlowContent.jsx) only when there's no line-item breakdown to
  // sum at all (very old bookings), so those render unchanged.
  const grandTotal = chargeTypeKeys.length
    ? chargeTypeKeys.reduce((s, k) => s + groupTotal(k), 0) + fallbackGstAmount
    : totalAmount;

  // The carrier's product/commodity block (product/commodity codes, SHC, DG
  // flag). Null for older/manual bookings that never carried one — the UI then
  // simply omits those chips and cells instead of rendering "N/A" rows.
  const productDetails = resolveProductCommodityDetails(nodeData);

  // ── Render-only derived values ─────────────────────────────────

  const originFlag           = getFlag(origin !== 'N/A' ? origin : '');
  const destFlag             = getFlag(destination !== 'N/A' ? destination : '');
  const airlineLogoUrl       = resolveStoredAirlineLogo(nodeData) || masterCarrierLogo;
  const shipmentReferenceNum = nodeData?.shipmentReferenceNum;

  const surface       = isDark ? '#1C1917' : '#fff';
  const textPrimary   = isDark ? '#F5F5F4' : '#1C1917';
  const textSecondary = isDark ? '#A8A29E' : '#78716C';
  const textMuted     = isDark ? '#78716C' : '#A8A29E';

  // The booking's stored commodity description ("Fats and oils (Perishables)")
  // is what the shipper actually booked, so it wins over the carrier block's
  // coarser natureOfGoods ("PERISHABLE") below. The "General Cargo" placeholder
  // substituted above for commodity-less bookings must not mask a real
  // natureOfGoods, so it's dropped here — same precedence as
  // CommodityDetailsSection in BookingDetailUI.jsx.
  const describedCommodity =
    /^general cargo$/i.test((commodityLabel || '').trim()) ? '' : commodityLabel;

  // Commodity Details cells — only what the booking actually carries, so older
  // /manual bookings without a carrier product block get a short row instead of
  // a wall of "N/A".
  //
  // Manual quotes are the exception: they have no carrier product block at all,
  // so a plain general-cargo one used to drop the whole section (every cell
  // filtered out, since describedCommodity deliberately discards the "General
  // Cargo" placeholder above). Nature of Goods therefore falls back to
  // commodityLabel for them, which puts "General Cargo" on the section instead
  // of hiding it. The codes stay blank rather than defaulted — showing a
  // "9999"/"GCR" the carrier never returned would be inventing data.
  const commodityCells = [
    ['Commodity Code',         productDetails?.commodityCode],
    ['Nature of Goods',        describedCommodity || productDetails?.natureOfGoods ||
                               (isManualQuoteRecord(nodeData) ? commodityLabel : '')],
    ['Product Code',           productDetails?.productCode],
    ['Special Handling Codes', productDetails?.specialHandlingCodes?.join(', ')],
    ['Density Group',          productDetails?.densityGroup],
    ['Service Code',           productDetails?.serviceCode],
    ['Temperature Range',      temperatureText],
  ].filter(([, value]) => value);

  const hasCharges = chargeTypeKeys.length > 0 || fallbackGstAmount > 0;

  // ── Query-mode cells ──────────────────────────────────────────────────────
  // What replaces "Booking Details" (Departure Date / Shipment Ref. No.) when
  // nothing has been booked yet. Empty entries are dropped rather than rendered
  // as "N/A", exactly like commodityCells above, so a sparse query shows a short
  // row instead of a wall of blanks.
  const preferredAirlines = isQueryMode
    ? preferredAirlineLabels(nodeData?.preferred_airline)
    : [];
  // isValid() guards the cell against rendering a literal "Invalid date" when
  // the field is present but not parsable.
  const quoteExpiryText =
    isQueryMode && nodeData?.quoteExpiry && moment(nodeData.quoteExpiry).isValid()
      ? moment(nodeData.quoteExpiry).utcOffset('+05:30').format('DD MMM YYYY, h:mm A')
      : '';
  const queryDetailCells = isQueryMode
    ? [
        ['Cargo Ready Date', cargoReadyDate ? cargoReadyDate.format('DD MMM YYYY') : ''],
        ['Incoterms',        nodeData?.paymentTerms?.incoTerms],
        ['Payment Terms',    nodeData?.paymentTerms?.paymentType],
        ['Preferred Airlines', preferredAirlines.join(', ')],
        // Only manual quotes carry an expiry; the label flips once it has
        // passed so the date doesn't read as a promise that still stands.
        [bookingStatus === 'Quote Expired' ? 'Quote Expired On' : 'Quote Valid Till', quoteExpiryText],
        // KAM/admin-only context — who the query belongs to, and the agent code
        // it was raised under. Customers already know both.
        ...(isKamSide
          ? [
              ['Customer',     nodeData?.userDetails?.full_name],
              ['Organisation', nodeData?.organizationDetails?.org_name],
              ['IATA / CASS',  nodeData?.iataCass],
            ]
          : []),
      ].filter(([, value]) => value)
    : [];

  // KAM/admin-only extra. Customers must never see the markup, so the tiles are
  // only even computed for KAM-side roles — the section below renders nothing
  // when the array is empty (older bookings carrying none of these fields).
  // Query mode has no booking, hence no applied markup, so it computes none.
  const markupTiles = isKamSide && !isQueryMode ? buildMarkupTiles(nodeData, markupConfig) : [];

  // ── Shipment card ───────────────────────────────────────────────
  // Held as an element rather than written inline below because where it goes
  // depends on the mode: in booking mode it sits beside the Charge Breakdown,
  // in query mode there is no charge breakdown, so it moves up to share the
  // row with Query Information instead of leaving that card's right half empty
  // and dropping the tiles onto a line of their own.
  const shipmentCard = (
    <SectionCard icon={<Inventory2Icon />} title={isQueryMode ? 'Shipment Summary' : 'Shipment & Rates'}>
      {/* 128px min keeps the longest labels ("Chargeable Wt.") on one line
          so the tiles stay a uniform height. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))', gap: '7px' }}>
        {!isQueryMode && (
          <StatCard label="Per KG Rate" value={ratePerkilo != null ? Number(ratePerkilo).toFixed(3) : null} unit="INR / KG" />
        )}
        {!isQueryMode && (
          <StatCard label="Net Amount"  value={grandTotal != null ? Number(grandTotal).toFixed(3) : null} unit="INR" />
        )}
        <StatCard label="Total Weight"   value={totalWeight != null ? Math.round(totalWeight) : null} unit={weightUnit} />
        <StatCard label="Vol. Weight"    value={volumeWt} unit="KG" />
        <StatCard label="Chargeable Wt." value={chargeableWt != null ? Math.round(chargeableWt) : null} unit={chargeableWtUnit} highlight />
        <StatCard
          label="Total Volume"
          value={totalVolume != null ? Number(totalVolume).toFixed(3) : null}
          unit={volumeUnit === 'CFT' && totalVolume != null
            ? `CFT (${(Number(totalVolume) * 0.0283168).toFixed(2)} CBM)`
            : volumeUnit}
        />
        <StatCard label="Total Pieces" value={totalPieces != null ? String(totalPieces) : null} unit="PCS" />
      </Box>
    </SectionCard>
  );

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: '10px', sm: '14px' },
        // No top padding of its own: PageLayout's content column already puts
        // a 16px gap (gap-4) between the header card and the routed page, and
        // stacking another 14px on top of it left a wide empty band above the
        // route banner. Matches the ocean view.
        pt: 0,
        pb: '48px',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}
    >

      {/* ── Route banner ─────────────────────────────────────── */}
      {/* The page heading + AWB line and the status pill that used to sit in a
          bar above this are gone: the arrow now rides the port-name row here,
          the AWB is already a Booking Information field, and both statuses
          moved into that card's header — the same arrangement the ocean view
          uses (components/BookingHistory/OceanCargoDetails.jsx). */}
      <Box
        sx={{
          bgcolor: isDark ? '#1C1610' : C.amberTint,
          border: `1.5px solid ${C.amberBorder}`,
          borderRadius: '14px',
          p: { xs: '11px 12px', sm: '12px 14px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '7px',
        }}
      >
        {/* Row 1 — back arrow, airline badge, route track. A bare arrow, not a
            bordered button: that chrome competed with the carrier tile beside
            it. */}
        <Box
          sx={{
            display: 'flex', flexDirection: 'row', alignItems: 'center',
            gap: '10px', minWidth: 0,
          }}
        >
          <IconButton
            onClick={handleBack}
            size="small"
            aria-label="Go back"
            disableRipple
            sx={{
              p: 0, width: 24, height: 24,
              color: textSecondary,
              '&:hover': { color: C.amber, bgcolor: 'transparent' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Airline logo */}
          <Box
            sx={{
              width: 40, height: 40, flexShrink: 0,
              border: `1.5px solid ${C.amberBorder}`, borderRadius: '6px',
              bgcolor: surface,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(28,25,23,.06)', overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={airlineLogoUrl || airlineDefault}
              alt="Airline logo"
              sx={{ width: '100%', height: '100%', objectFit: 'contain', p: '6px' }}
              onError={(e) => { e.target.src = airlineDefault; }}
            />
          </Box>

          {/* Route track */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ fontSize: { xs: '17px', sm: '20px' }, fontWeight: 800, color: textPrimary, letterSpacing: '-.03em', lineHeight: 1 }}>
                {originFlag} {origin}
              </Typography>
              {depShort && <Typography sx={{ fontSize: '10px', color: textSecondary, fontWeight: 500, mt: '2px' }}>{depShort}</Typography>}
            </Box>

            {/* Dashed connector with plane */}
            <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', px: { xs: '8px', sm: '12px' } }}>
              <Box sx={{ width: '100%', borderTop: `2px dashed ${C.amber}`, opacity: .45 }} />
              <Box
                sx={{
                  position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
                  width: 24, height: 24, bgcolor: C.amber, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(245,158,11,.35)',
                }}
              >
                <FlightIcon sx={{ fontSize: 12, color: '#fff', transform: 'rotate(45deg)' }} />
              </Box>
            </Box>

            <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
              <Typography sx={{ fontSize: { xs: '17px', sm: '20px' }, fontWeight: 800, color: textPrimary, letterSpacing: '-.03em', lineHeight: 1 }}>
                {destination} {destFlag}
              </Typography>
              {arrShort && <Typography sx={{ fontSize: '10px', color: textSecondary, fontWeight: 500, mt: '2px' }}>{arrShort}</Typography>}
            </Box>
          </Box>
        </Box>

        {/* Row 2 — carrier, commodity, temperature, the carrier's
            product/commodity codes (absent on older/manual bookings) and the
            booking's status. Its own row under the whole of row 1, so the
            arrow and badge stay on the port-name line rather than being
            centred against both. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
          {/* No carrier has been chosen on an unbooked query — the airline
              tile above falls back to the generic plane icon for the same
              reason. Whatever airlines the shipper asked for take its place
              when they named any. */}
          {!isQueryMode && (
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: textPrimary, mr: '2px' }}>{carrierName}</Typography>
          )}
          {isQueryMode && preferredAirlines.length > 0 && (
            <TagChip tone="plain" title={`Preferred airlines: ${preferredAirlines.join(', ')}`}>
              <Box component="span" sx={{ color: textMuted }}>Preferred:</Box>
              <Box component="span" sx={{ fontWeight: 700 }}>{preferredAirlines.join(', ')}</Box>
            </TagChip>
          )}
          {commodityLabel && <TagChip title={commodityLabel}>📦 {commodityLabel}</TagChip>}
          {temperatureText && (
            <TagChip tone="teal" title={`Temperature range: ${temperatureText}`}>
              <AcUnitIcon sx={{ fontSize: 11 }} />{temperatureText}
            </TagChip>
          )}
          {[
            ['Commodity Code', productDetails?.commodityCode],
            ['Product',        productDetails?.productCode],
            ['SHC',            productDetails?.specialHandlingCodes?.join(', ')],
            ['Density Group',  productDetails?.densityGroup],
            ['Service Code',   productDetails?.serviceCode],
          ]
            .filter(([, value]) => value)
            .map(([name, value]) => (
              <TagChip key={name} tone="plain" title={`${name}: ${value}`}>
                <Box component="span" sx={{ color: textMuted }}>{name}:</Box>
                <Box component="span" sx={{ fontWeight: 700 }}>{value}</Box>
              </TagChip>
            ))}
          {productDetails?.dgShipment && (
            <TagChip tone="red" title="Dangerous goods shipment">DG SHIPMENT</TagChip>
          )}
          <StatusPill status={bookingStatus} />
        </Box>
      </Box>

      {/* ── Booking Information (+ Shipment Summary in query mode) ─ */}
      {/* A query carries so few fields that the card left most of its width
          empty while the shipment tiles sat on a line of their own below it.
          Query mode therefore pairs the two on one row; booking mode keeps the
          information card full-width, because its shipment card belongs beside
          the Charge Breakdown further down. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isQueryMode ? { xs: '1fr', lg: '1fr 1fr' } : '1fr',
          gap: '10px',
          alignItems: 'stretch',
        }}
      >
        <SectionCard
          icon={<CalendarMonthIcon />}
          title={isQueryMode ? 'Query Information' : 'Booking Information'}
          headerRight={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {/* Payment first, then booking status — nothing has been paid on an
                  unbooked query, so query mode shows the quote status alone. */}
              {!isQueryMode && nodeData?.payment_status && (
                <StatusPill status={nodeData.payment_status} />
              )}
              <StatusPill status={bookingStatus} />
            </Box>
          }
        >
          {/* Query mode drops back to two columns from `lg` up — that's where the
              card becomes a half-width column beside the shipment tiles, and
              four columns in half a row squeezed every value onto three lines. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isQueryMode
                ? { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }
                : { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
            }}
          >
            <InfoSubLabel isFirst>Route</InfoSubLabel>
            <InfoCell label="Origin"       value={origin} />
            <InfoCell label="Destination"  value={destination} />
            {/* Carrier and AWB only exist once the shipment has been booked. */}
            {!isQueryMode && <InfoCell label="Carrier Name" value={carrierName} />}
            {!isQueryMode && <InfoCell label="AWB Number"   value={awbNumber || 'N/A'} />}
  
            {commodityCells.length > 0 && (
              <>
                <InfoSubLabel>Commodity Details</InfoSubLabel>
                {commodityCells.map(([label, value]) => (
                  <InfoCell key={label} label={label} value={value} />
                ))}
                {productDetails?.dgShipment && (
                  <InfoCell
                    label="DG Shipment"
                    value={
                      <Box component="span" sx={{ display: 'inline-block', px: '7px', py: '1px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, bgcolor: C.redTint, color: C.red, border: `1px solid ${C.redBorder}` }}>
                        Yes
                      </Box>
                    }
                  />
                )}
              </>
            )}
  
            {/* Query mode swaps the booking-only Departure Date / Shipment Ref.
                for what the query itself carries — see queryDetailCells. The
                sub-heading is dropped entirely when the query is so sparse that
                nothing survives the filter. */}
            {isQueryMode ? (
              queryDetailCells.length > 0 && (
                <>
                  <InfoSubLabel>Query Details</InfoSubLabel>
                  {queryDetailCells.map(([label, value]) => (
                    <InfoCell key={label} label={label} value={value} />
                  ))}
                </>
              )
            ) : (
              <>
                <InfoSubLabel>Booking Details</InfoSubLabel>
                <InfoCell label="Departure Date"    value={depFull} />
                <InfoCell label="Shipment Ref. No." value={shipmentReferenceNum || 'N/A'} />
                {/* Internal — customers never see which space bucket the
                    offline rate was booked against. */}
                {isKamSide && spaceAvailabilityLabel && (
                  <InfoCell label="Space Availability" value={spaceAvailabilityLabel} />
                )}
              </>
            )}
          </Box>
        </SectionCard>

        {isQueryMode && shipmentCard}
      </Box>

      {/* ── Charge Breakdown  +  Shipment & Rates ────────────── */}
      {/* Query mode renders neither: there is no accepted rate to break down,
          and its shipment tiles already sit beside Query Information above. */}
      {/* alignItems: 'stretch' (the grid default) so the two cards share the
          row's height instead of each ending at its own content — the tile
          grid is always much shorter than the charge breakdown, and a card
          floating two-thirds up the row read as a layout bug. */}
      {!isQueryMode && (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '10px', alignItems: 'stretch' }}>

        {/* Charge Breakdown */}
        <SectionCard icon={<PaymentsIcon />} title="Charge Breakdown">
          {hasCharges ? (
            <>
              {chargeTypeKeys.map((type) => {
                const items = groupedCharges[type] || [];
                const isGst = type === 'GST';

                return (
                  <React.Fragment key={type}>
                    {/* Taxable Amount sits immediately above the GST block */}
                    {isGst && <TaxableRow amount={formatrates(preGstSubtotal.toFixed(2))} />}

                    <ChargeGroup
                      name={type}
                      total={formatrates(groupTotal(type).toFixed(2))}
                      isGst={isGst}
                      items={items.map((charge) => {
                        // Older chargesBreakDown records only stored the total,
                        // so derive the per-kg hint from the chargeable weight.
                        const perKg =
                          safeNum(charge.ratePerkilo) ??
                          safeNum(charge.ratePerUnit) ??
                          safeNum(charge.ratePerKg) ??
                          safeNum(charge.chargeRate) ??
                          safeNum(charge.rate_per_unit) ??
                          calcPerKg(charge.Totalamount ?? charge.chargeAmount, chargeableWt);
                        const amount = charge.Totalamount ?? charge.chargeAmount;

                        return {
                          name:   charge.name || charge.chargeDescription || '—',
                          rate:   perKg != null && perKg > 0
                            ? `${formatrates(Number(perKg).toFixed(2))} /kg`
                            : null,
                          amount: amount != null ? formatrates(Number(amount).toFixed(2)) : '—',
                        };
                      })}
                    />
                  </React.Fragment>
                );
              })}

              {/* Fallback GST — bookings where GST was never itemised as its own
                  chargesBreakDown category (see NewBookingFlowContent.jsx). */}
              {fallbackGstAmount > 0 && (
                <>
                  <TaxableRow amount={formatrates(fallbackTaxableAmount.toFixed(2))} />
                  <ChargeGroup name="GST Amount (18%)" total={formatrates(fallbackGstAmount.toFixed(2))} isGst />
                </>
              )}

              {/* Net Amount */}
              <NetAmountBar
                amount={grandTotal != null ? formatrates(Number(grandTotal).toFixed(2)) : 'N/A'}
              />
            </>
          ) : (
            <Typography sx={{ textAlign: 'center', py: 4, color: textMuted, fontSize: '12px' }}>
              No charge breakdown available for this booking.
            </Typography>
          )}
        </SectionCard>

        {/* Shipment & Rates */}
        {shipmentCard}
      </Box>
      )}

      {/* ── Markup Details (KAM / admin only) ─────────────────── */}
      {/* The single difference between what a KAM and a customer see on this
          page. Deliberately rendered with the same SectionCard/StatCard
          primitives as everything above so the unified layout holds. */}
      {markupTiles.length > 0 && (
        <SectionCard icon={<PercentIcon />} title="Markup Details" badge="Internal">
          {/* 152px min, not the 128px used by Shipment & Rates: the longest
              label here ("Customer Category") needs ~132px of content width to
              stay on one line, and a wrapped label made that tile taller than
              the rest of the row. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(152px, 1fr))', gap: '7px' }}>
            {markupTiles.map((tile) => (
              <StatCard
                key={tile.label}
                label={tile.label}
                value={tile.value}
                unit={tile.unit}
                highlight={tile.highlight}
              />
            ))}
          </Box>
        </SectionCard>
      )}

      {/* ── Flight Details ────────────────────────────────────── */}
      {segments.length > 0 && (
        <SectionCard
          icon={<FlightIcon />}
          title="Flight Details"
          badge={`${segments.length} ${segments.length === 1 ? 'leg' : 'legs'}`}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 560, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {['Carrier No.', 'Scheduled Departure', 'Scheduled Arrival', 'Dep. Segment', 'Arr. Segment', 'Aircraft Type']
                    .map((h) => <TableCell key={h} sx={thSx}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {segments.map((seg, idx) => (
                  <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 'none' }, '&:hover': { bgcolor: isDark ? '#211E1B' : '#FAFAF9' } }}>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{carrierLabel(seg)}</TableCell>
                    <TableCell sx={tdSx(isDark)}>
                      {seg?.scheduledDepartureDate ? parseSegDate(seg.scheduledDepartureDate).format('DD MMM YYYY') : 'N/A'}
                    </TableCell>
                    <TableCell sx={tdSx(isDark)}>
                      {seg?.scheduledArrivalDate ? parseSegDate(seg.scheduledArrivalDate).format('DD MMM YYYY') : 'N/A'}
                    </TableCell>
                    <TableCell sx={tdSx(isDark)}><SegmentPill label={seg?.segmentOfDeparture || 'N/A'} variant="dep" /></TableCell>
                    <TableCell sx={tdSx(isDark)}><SegmentPill label={seg?.segmentOfArrival   || 'N/A'} variant="arr" /></TableCell>
                    <TableCell sx={{ ...tdSx(isDark), color: textSecondary }}>
                      {seg?.carrierAircraftType || seg?.iataEqpType || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ── Shipment Dimensions ───────────────────────────────── */}
      {packages.length > 0 && (
        <SectionCard
          icon={<StraightenIcon />}
          title="Shipment Dimensions"
          badge={`${packages.length} ${packages.length === 1 ? 'item' : 'items'}`}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 640, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  {['Pieces', 'Length', 'Width', 'Height', 'Dim. Unit', 'WT / Piece', 'WT. Unit', 'Turnable', 'Stackable']
                    .map((h) => <TableCell key={h} sx={thSx}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg, idx) => (
                  <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 'none' }, '&:hover': { bgcolor: isDark ? '#211E1B' : '#FAFAF9' } }}>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{pkg?.units ?? 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{pkg?.length ?? 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{pkg?.width  ?? 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{pkg?.height ?? 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), color: textSecondary }}>{pkg?.unit || 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), fontWeight: 700 }}>{pkg?.weight ?? 'N/A'}</TableCell>
                    <TableCell sx={{ ...tdSx(isDark), color: textSecondary }}>{pkg?.weightunit || 'KG'}</TableCell>
                    <TableCell sx={tdSx(isDark)}><BoolPill value={pkg?.turnable} /></TableCell>
                    <TableCell sx={tdSx(isDark)}><BoolPill value={pkg?.stackable} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

    </Box>
  );
};

export default BookingDetailPage;
